import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Reveal, EASE } from "@/components/primitives";
import { STAGES } from "@/data/process";

const ACCENT = "#C8554F";

export default function Process() {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);
  const navigate = useNavigate();
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const active = hovered ?? pinned;

  return (
    <section id="approach" className="grain relative bg-ink px-6 py-24 lg:px-12 lg:py-32" data-testid="process-section">
      <div className="relative z-10 mx-auto max-w-[1440px]">
        <Reveal>
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-paper/60">
            <span style={{ color: ACCENT }}>07</span> · Our Approach
          </p>
        </Reveal>
        <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal delay={0.1} className="lg:col-span-7">
            <h2 className="font-serif text-4xl leading-tight text-paper sm:text-5xl">
              How we build:<br />
              <em className="italic">The ARA TURNKEY Way</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="max-w-md text-sm leading-relaxed text-paper/70 lg:ml-auto">
              A 6-stage process where nothing is hidden, nothing is skipped, and every decision is yours to make.
            </p>
          </Reveal>
        </div>

        <div ref={ref} className="relative mt-20 hidden md:block" data-testid="process-diagram-desktop">
          <div className="absolute left-0 right-0 top-[7px] h-px bg-paper/20" />
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 2.2, ease: EASE }}
            className="absolute left-0 right-0 top-[6px] h-[3px] origin-left"
            style={{ backgroundColor: ACCENT }}
            data-testid="process-progress-fill"
          />
          <div className="grid grid-cols-6 gap-6">
            {STAGES.map((s, i) => (
              <div
                key={s.name}
                className="flex cursor-pointer flex-col"
                data-testid={`process-stage-${i + 1}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setPinned(pinned === i ? null : i)}
              >
                <motion.span
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.25 + i * 0.34, duration: 0.4, ease: EASE }}
                  className="relative z-10 flex h-[15px] w-[15px] items-center justify-center rounded-full border-2 bg-ink transition-transform duration-300"
                  style={{ borderColor: ACCENT, transform: active === i ? "scale(1.5)" : undefined }}
                >
                  <span className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: ACCENT }} />
                </motion.span>
                <span className="mt-7 font-sans text-sm font-bold" style={{ color: ACCENT }}>
                  0{i + 1}
                </span>
                <span className="mt-2 font-serif text-xl leading-snug text-paper lg:text-2xl">{s.name}</span>
                <motion.div
                  initial={false}
                  animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="overflow-hidden"
                  data-testid={`process-stage-desc-${i + 1}`}
                >
                  <p className="pt-3 text-xs leading-relaxed text-paper/65">{s.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-16 md:hidden" data-testid="process-diagram-mobile">
          <div className="absolute bottom-3 left-[7px] top-3 w-px bg-paper/20" />
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 2, ease: EASE }}
            className="absolute bottom-3 left-[6px] top-3 w-[3px] origin-top"
            style={{ backgroundColor: ACCENT }}
          />
          <div className="flex flex-col gap-8">
            {STAGES.map((s, i) => (
              <div
                key={s.name}
                className="relative pl-10"
                data-testid={`process-stage-mobile-${i + 1}`}
                onClick={() => setPinned(pinned === i ? null : i)}
              >
                <span
                  className="absolute left-0 top-1 flex h-[15px] w-[15px] items-center justify-center rounded-full border-2 bg-ink"
                  style={{ borderColor: ACCENT }}
                >
                  <span className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: ACCENT }} />
                </span>
                <span className="font-sans text-sm font-bold" style={{ color: ACCENT }}>
                  0{i + 1}
                </span>
                <p className="mt-1 font-serif text-2xl text-paper">{s.name}</p>
                <motion.div
                  initial={false}
                  animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="pt-2 text-xs leading-relaxed text-paper/65">{s.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => navigate("/approach")}
              data-testid="approach-page-button"
              className="group flex items-center gap-3 rounded-full border border-paper/30 px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-paper"
            >
              Our Approach
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
